import { Injectable, signal } from '@angular/core';
import { HttpClient, HttpErrorResponse, HttpHeaders } from '@angular/common/http';
import { Observable, map, catchError, of, firstValueFrom } from 'rxjs';
import { LicenciaM31 } from '../models/licencia-m31.model';
import { environment } from '../environments/environment';

export interface AuthenticatedUser {
  name: string;
  email: string;
  companyName?: string;
  photoUrl?: string;
}

interface LoginApiResponse {
  IsAuthenticated: boolean;
  Message: string;
  User: {
    Name: string;
    Email: string;
    CompanyName?: string;
  } | null;
  Licencias: LicenciaM31[];
  RequiresMicrosoftLogin?: boolean;
  LicenseSessionTicket?: string;
  LicenseSessionExpiresUtc?: string;
}

interface RefreshSessionApiResponse {
  LicenseSessionTicket?: string;
  LicenseSessionExpiresUtc?: string;
}

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  /** Agentes que consultan datos del tenant del usuario y por eso exigen login de Microsoft. */
  private static readonly AgentesQueRequierenMicrosoft = ['MIRACH', 'DELTA'];

  /** Quita la ApiKey de las licencias antes de guardarlas o usarlas en el navegador. */
  private static sinSecretos(licencias: LicenciaM31[]): LicenciaM31[] {
    return (licencias ?? []).map(({ ApiKey: _omitida, ...resto }) => resto as LicenciaM31);
  }

  private static readonly RequiresMicrosoftLoginStorageKey = 'm31_requires_microsoft_login';
  private static readonly SessionUserStorageKey = 'm31_session_user';
  private static readonly SessionLicensesStorageKey = 'm31_session_licencias';
  private static readonly MsalTokenStorageKey = 'm31_msal_token';
  private static readonly LicenseSessionTicketStorageKey = 'm31_license_session_ticket';
  private static readonly LicenseSessionExpiresUtcStorageKey = 'm31_license_session_expires_utc';

  // --- ESTADO GLOBAL DE LA SESIÓN ---
  isLoggedIn = signal<boolean>(false);
  currentUser = signal<AuthenticatedUser | null>(null);
  licencias = signal<LicenciaM31[]>([]);
  requiresMicrosoftLogin = signal<boolean>(environment.auth.requireMicrosoftLoginAfterSiic);
  lastLoginError = signal<string>('');
  licenseSessionTicket = signal<string | null>(null);
  licenseSessionExpiresAt = signal<number | null>(null);

  msalAccessToken = signal<string | null>(null);

  /**
   * La sesión de Microsoft caducó y no se pudo renovar sola.
   *
   * Es distinto de "no hay token": la sesión del SIIC sigue siendo válida y el usuario sigue
   * dentro de la plataforma. Solo hace falta que vuelva a identificarse contra Microsoft, y la
   * interfaz se lo ofrece en vez de dejarlo con el chat muerto.
   */
  readonly microsoftSessionExpired = signal<boolean>(false);

  /**
   * Fecha de caducidad que viene dentro del propio token (claim `exp`), en milisegundos.
   * Devuelve null si el token no es legible: en ese caso se trata como si no caducara y será
   * el backend quien lo rechace.
   */
  static fechaCaducidadToken(token: string | null): number | null {
    const partes = (token ?? '').split('.');
    if (partes.length < 2) {
      return null;
    }

    try {
      const relleno = '='.repeat((4 - (partes[1].length % 4)) % 4);
      const cuerpo = JSON.parse(atob(partes[1].replace(/-/g, '+').replace(/_/g, '/') + relleno));
      return typeof cuerpo?.exp === 'number' ? cuerpo.exp * 1000 : null;
    } catch {
      return null;
    }
  }

  /** Milisegundos que le quedan de vida al token actual; null si no se puede saber. */
  minutosRestantesTokenMicrosoft(): number | null {
    const caducidad = AuthService.fechaCaducidadToken(this.msalAccessToken());
    return caducidad === null ? null : (caducidad - Date.now()) / 60000;
  }

  /** Lo marca como caducado para que la interfaz ofrezca reconectar. */
  marcarSesionMicrosoftCaducada(): void {
    this.microsoftSessionExpired.set(true);
  }

  constructor(private http: HttpClient) {
    this.restoreStoredSession();
  }

  /**
   * Reconstruye la sesión a partir de lo guardado en el navegador. Devuelve true si quedó activa.
   *
   * Se ejecuta al arrancar y también cuando llega el token de Microsoft: en el login de un usuario
   * que exige Microsoft, la sesión del SIIC ya está guardada pero todavía no hay token, así que el
   * primer intento no puede activarla. Sin este segundo intento la pantalla se quedaba en el login
   * hasta que el usuario recargaba a mano.
   */
  private restoreStoredSession(): boolean {
    const storedUser = this.getStoredValue(AuthService.SessionUserStorageKey);
    const storedLicencias = this.getStoredValue(AuthService.SessionLicensesStorageKey);
    const storedMsalToken = this.getStoredValue(AuthService.MsalTokenStorageKey);
    const storedRequiresMicrosoftLogin = this.getStoredValue(AuthService.RequiresMicrosoftLoginStorageKey);
    const storedLicenseSessionTicket = this.getStoredValue(AuthService.LicenseSessionTicketStorageKey);
    const storedLicenseSessionExpiresUtc = this.getStoredValue(AuthService.LicenseSessionExpiresUtcStorageKey);
    const licenseSessionExpiresAt = this.parseExpiration(storedLicenseSessionExpiresUtc);
    const hasStoredSession = Boolean(storedUser && storedLicencias);
    let restoredLicencias: LicenciaM31[] = [];

    if (storedLicencias) {
      try {
        restoredLicencias = AuthService.sinSecretos(JSON.parse(storedLicencias) as LicenciaM31[]);
        // Purga las ApiKey que quedaron guardadas por versiones anteriores de la plataforma.
        const limpio = JSON.stringify(restoredLicencias);
        if (limpio !== storedLicencias) {
          sessionStorage.setItem(AuthService.SessionLicensesStorageKey, limpio);
          localStorage.setItem(AuthService.SessionLicensesStorageKey, limpio);
        }
      } catch {
        restoredLicencias = [];
      }
    }

    const requiresMicrosoftLogin = storedRequiresMicrosoftLogin === 'true'
      ? true
      : storedRequiresMicrosoftLogin === 'false'
        ? false
        : this.requiresMicrosoftLoginForLicenses(restoredLicencias);
    const isMsalRedirectInProgress = this.isMsalRedirectInProgress();
    const canRestoreSession = hasStoredSession
      && !this.isExpired(licenseSessionExpiresAt)
      && (!requiresMicrosoftLogin || Boolean(storedMsalToken) || isMsalRedirectInProgress);

    if (canRestoreSession) {
      const userJson = storedUser as string;
      const licenciasJson = storedLicencias as string;

      this.isLoggedIn.set(true);
      this.currentUser.set(JSON.parse(userJson) as AuthenticatedUser);
      this.licencias.set(restoredLicencias.length > 0 ? restoredLicencias : JSON.parse(licenciasJson));
      this.requiresMicrosoftLogin.set(requiresMicrosoftLogin);
      this.licenseSessionTicket.set(storedLicenseSessionTicket);
      this.licenseSessionExpiresAt.set(licenseSessionExpiresAt);

      if (storedMsalToken) {
        this.msalAccessToken.set(storedMsalToken);
      }
      return true;
    }

    if (hasStoredSession && this.isExpired(licenseSessionExpiresAt)) {
      this.clearRejectedLoginState();
    }

    // Keep SIIC session data so MSAL redirect callback can complete without forcing a new SIIC login.
    return false;
  }

  /**
   * Renueva la sesión mientras el usuario sigue trabajando. Reemite el ticket de licencias con
   * caducidad nueva, de modo que la sesión se comporte como deslizante: solo se cierra tras un
   * periodo real de inactividad, no a las X horas de haber entrado.
   */
  async refreshSession(): Promise<boolean> {
    const email = this.currentUser()?.email?.trim();
    const ticket = this.licenseSessionTicket()?.trim();
    if (!email || !ticket) {
      return false;
    }

    let headers = new HttpHeaders()
      .set('X-M31-User-Email', email)
      .set('X-M31-License-Ticket', ticket);

    const token = this.msalAccessToken();
    if (token) {
      headers = headers.set('Authorization', `Bearer ${token}`);
    }

    try {
      const respuesta = await firstValueFrom(
        this.http.post<RefreshSessionApiResponse>(
          `${environment.apiUrl}/auth/refresh-session`,
          { LicenseSessionTicket: ticket },
          { headers }
        )
      );

      this.licenseSessionTicket.set(respuesta.LicenseSessionTicket?.trim() || null);
      this.licenseSessionExpiresAt.set(this.parseExpiration(respuesta.LicenseSessionExpiresUtc));
      this.storeLicenseSessionTicket(respuesta.LicenseSessionTicket);
      this.storeLicenseSessionExpiration(respuesta.LicenseSessionExpiresUtc);
      return true;
    } catch {
      // Si no se pudo renovar, los temporizadores siguen con la caducidad anterior.
      return false;
    }
  }

  requiresMicrosoftLoginForCurrentUser(): boolean {
    return this.requiresMicrosoftLogin();
  }

  setMicrosoftToken(token: string): void {
    if (this.msalAccessToken() === token) {
      return;
    }

    this.msalAccessToken.set(token);
    this.microsoftSessionExpired.set(false);
    sessionStorage.setItem(AuthService.MsalTokenStorageKey, token);
    localStorage.setItem(AuthService.MsalTokenStorageKey, token);

    // El token era justo lo que faltaba para dar por válida la sesión guardada: se reintenta.
    if (!this.isLoggedIn()) {
      this.restoreStoredSession();
    }
  }


  login(email: string, password: string): Observable<boolean> {
    if (!environment.production) {
      console.log(`[AUTH] Iniciando flujo de validación para: ${email}`);
    }

    return this.http.post<LoginApiResponse>(`${environment.apiUrl}/auth/login`, { email, password }).pipe(
      map((response: LoginApiResponse) => {
        if (!response?.IsAuthenticated) {
          throw new Error(response?.Message || 'Credenciales incorrectas.');
        }

        if (!environment.production) {
          console.log('[AUTH] Sesión confirmada. Recuperando licencias...');
        }

        const datos = response.Licencias ?? [];
        const requiresMicrosoftLogin = typeof response.RequiresMicrosoftLogin === 'boolean'
          ? response.RequiresMicrosoftLogin
          : this.requiresMicrosoftLoginForLicenses(datos);

        this.licencias.set(datos);
        this.requiresMicrosoftLogin.set(requiresMicrosoftLogin);
        this.isLoggedIn.set(true);
        this.lastLoginError.set('');
        this.licenseSessionTicket.set(response.LicenseSessionTicket?.trim() || null);
        this.licenseSessionExpiresAt.set(this.parseExpiration(response.LicenseSessionExpiresUtc));

        const user = {
          name: response.User?.Name ?? email.split('@')[0],
          email: response.User?.Email ?? email,
          companyName: response.User?.CompanyName?.trim() ?? ''
        };

        this.currentUser.set(user);

        const serializedUser = JSON.stringify(this.toStoredUser(user));
        const serializedLicenses = JSON.stringify(AuthService.sinSecretos(datos));

        sessionStorage.setItem(AuthService.SessionUserStorageKey, serializedUser);
        sessionStorage.setItem(AuthService.SessionLicensesStorageKey, serializedLicenses);
        localStorage.setItem(AuthService.SessionUserStorageKey, serializedUser);
        localStorage.setItem(AuthService.SessionLicensesStorageKey, serializedLicenses);
        sessionStorage.setItem(AuthService.RequiresMicrosoftLoginStorageKey, String(requiresMicrosoftLogin));
        localStorage.setItem(AuthService.RequiresMicrosoftLoginStorageKey, String(requiresMicrosoftLogin));
        this.storeLicenseSessionTicket(response.LicenseSessionTicket);
        this.storeLicenseSessionExpiration(response.LicenseSessionExpiresUtc);

        return true;
      }),
      catchError((error: unknown) => {
        const message = this.resolveErrorMessage(error);
        console.error('[AUTH ERROR] El acceso fue denegado:', message);
        this.lastLoginError.set(message);
        this.clearRejectedLoginState();
        return of(false);
      })
    );
  }

  logout(): void {
    this.isLoggedIn.set(false);
    this.currentUser.set(null);
    this.licencias.set([]);
    this.requiresMicrosoftLogin.set(environment.auth.requireMicrosoftLoginAfterSiic);
    this.msalAccessToken.set(null);
    this.microsoftSessionExpired.set(false);
    this.licenseSessionTicket.set(null);
    this.licenseSessionExpiresAt.set(null);
    // Se borran solo las claves de M31. Ni sessionStorage.clear() ni las claves de cliente y
    // tenant de MSAL: eso obligaría a reconfigurar MSAL y a pedir credenciales otra vez, que es
    // justo lo que queremos evitar al cerrar sesión.
    for (const clave of [
      AuthService.SessionUserStorageKey,
      AuthService.SessionLicensesStorageKey,
      AuthService.MsalTokenStorageKey,
      AuthService.RequiresMicrosoftLoginStorageKey,
      AuthService.LicenseSessionTicketStorageKey,
      AuthService.LicenseSessionExpiresUtcStorageKey
    ]) {
      sessionStorage.removeItem(clave);
      localStorage.removeItem(clave);
    }

    AuthService.borrarConversaciones();
  }

  /**
   * Borra las conversaciones guardadas de todos los agentes al cerrar sesión.
   *
   * Sin esto, al volver a entrar se veían los mensajes anteriores en pantalla, pero el hilo del
   * agente ya había caducado: el primer mensaje abría una conversación nueva y el agente no
   * recordaba nada de lo que el usuario seguía leyendo encima.
   */
  private static borrarConversaciones(): void {
    const prefijo = 'm31_foundry_';

    for (const almacen of [sessionStorage, localStorage]) {
      const claves: string[] = [];
      for (let i = 0; i < almacen.length; i++) {
        const clave = almacen.key(i);
        if (clave?.startsWith(prefijo)) { claves.push(clave); }
      }
      claves.forEach(clave => almacen.removeItem(clave));
    }
  }

  setProfilePhoto(photoUrl: string | null): void {
    const user = this.currentUser();
    if (!user) {
      return;
    }

    this.currentUser.set({
      ...user,
      photoUrl: photoUrl?.trim() || undefined
    });
  }

  clearProfilePhoto(): void {
    this.setProfilePhoto(null);
  }

  private clearRejectedLoginState(): void {
    this.isLoggedIn.set(false);
    this.currentUser.set(null);
    this.licencias.set([]);
    this.requiresMicrosoftLogin.set(environment.auth.requireMicrosoftLoginAfterSiic);
    this.msalAccessToken.set(null);
    this.microsoftSessionExpired.set(false);
    this.licenseSessionTicket.set(null);
    this.licenseSessionExpiresAt.set(null);
    sessionStorage.removeItem(AuthService.SessionUserStorageKey);
    sessionStorage.removeItem(AuthService.SessionLicensesStorageKey);
    sessionStorage.removeItem(AuthService.MsalTokenStorageKey);
    sessionStorage.removeItem(AuthService.RequiresMicrosoftLoginStorageKey);
    localStorage.removeItem(AuthService.SessionUserStorageKey);
    localStorage.removeItem(AuthService.SessionLicensesStorageKey);
    localStorage.removeItem(AuthService.MsalTokenStorageKey);
    localStorage.removeItem(AuthService.RequiresMicrosoftLoginStorageKey);
    sessionStorage.removeItem(AuthService.LicenseSessionTicketStorageKey);
    localStorage.removeItem(AuthService.LicenseSessionTicketStorageKey);
    sessionStorage.removeItem(AuthService.LicenseSessionExpiresUtcStorageKey);
    localStorage.removeItem(AuthService.LicenseSessionExpiresUtcStorageKey);
  }

  private toStoredUser(user: AuthenticatedUser): Omit<AuthenticatedUser, 'photoUrl'> {
    return {
      name: user.name,
      email: user.email,
      companyName: user.companyName?.trim() || undefined
    };
  }

  private storeLicenseSessionTicket(ticket?: string): void {
    const normalizedTicket = ticket?.trim();
    if (!normalizedTicket) {
      sessionStorage.removeItem(AuthService.LicenseSessionTicketStorageKey);
      localStorage.removeItem(AuthService.LicenseSessionTicketStorageKey);
      return;
    }

    sessionStorage.setItem(AuthService.LicenseSessionTicketStorageKey, normalizedTicket);
    localStorage.setItem(AuthService.LicenseSessionTicketStorageKey, normalizedTicket);
  }

  private storeLicenseSessionExpiration(expiration?: string): void {
    const expiresAt = this.parseExpiration(expiration);
    if (!expiresAt) {
      sessionStorage.removeItem(AuthService.LicenseSessionExpiresUtcStorageKey);
      localStorage.removeItem(AuthService.LicenseSessionExpiresUtcStorageKey);
      return;
    }

    const value = new Date(expiresAt).toISOString();
    sessionStorage.setItem(AuthService.LicenseSessionExpiresUtcStorageKey, value);
    localStorage.setItem(AuthService.LicenseSessionExpiresUtcStorageKey, value);
  }

  private parseExpiration(value?: string | null): number | null {
    const timestamp = value ? Date.parse(value) : Number.NaN;
    return Number.isFinite(timestamp) ? timestamp : null;
  }

  private isExpired(expiresAt: number | null): boolean {
    return expiresAt !== null && expiresAt <= Date.now();
  }

  tieneLicencia(nombreProducto: string): boolean {
    return Boolean(this.findMatchingLicense(nombreProducto));
  }

  findMatchingLicense(nombreProducto: string): LicenciaM31 | undefined {
    const search = this.normalizeLicenseKey(nombreProducto);
    if (!search) {
      return undefined;
    }

    const normalizedLicenses = this.licencias().map((license) => ({
      license,
      technicalName: this.normalizeLicenseKey(license.NombreTecnico)
    }));

    const exactMatch = normalizedLicenses.find((item) => item.technicalName === search);
    if (exactMatch) {
      return exactMatch.license;
    }

    // Keep fuzzy matching only for sufficiently specific keys to avoid collisions like MU vs MUCUSTOMBRAND.
    if (search.length < 4) {
      return undefined;
    }

    const fuzzyMatch = normalizedLicenses.find((item) =>
      this.licenseMatches(item.license, nombreProducto)
    );

    return fuzzyMatch?.license;
  }

  licenseMatches(license: LicenciaM31, nombreProducto: string): boolean {
    const search = this.normalizeLicenseKey(nombreProducto);
    const technicalName = this.normalizeLicenseKey(license.NombreTecnico ?? '');

    if (!search || !technicalName) {
      return false;
    }

    if (technicalName === search) {
      return true;
    }

    if (search.length < 4 || technicalName.length < 4) {
      return false;
    }

    return technicalName.includes(search) || search.includes(technicalName);
  }

  private normalizeLicenseKey(value: string): string {
    return value
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toUpperCase()
      .replace(/[^A-Z0-9]/g, '');
  }

  /**
   * Solo MIRACH y DELTA necesitan identidad de Microsoft: consultan datos del tenant del
   * usuario (SharePoint y Dynamics). Quien no tenga ninguno de esos dos entra solo con SIIC.
   */
  private requiresMicrosoftLoginForLicenses(licencias: LicenciaM31[]): boolean {
    if (!environment.auth.requireMicrosoftLoginAfterSiic) {
      return false;
    }

    return licencias.some((licencia) => {
      const nombre = this.normalizeLicenseKey(licencia.NombreTecnico ?? '');
      return AuthService.AgentesQueRequierenMicrosoft.some((agente) => nombre.includes(agente));
    });
  }

  private isMsalRedirectInProgress(): boolean {
    if (typeof window === 'undefined') {
      return false;
    }

    const query = window.location.search.toLowerCase();
    const hash = window.location.hash.toLowerCase();
    const source = `${query}&${hash}`;

    return source.includes('code=') && source.includes('state=');
  }

  private resolveErrorMessage(error: unknown): string {
    if (error instanceof HttpErrorResponse) {
      if (typeof error.error === 'string' && error.error.trim()) {
        return error.error;
      }

      if (error.error?.Message) {
        return error.error.Message as string;
      }

      return error.message;
    }

    if (error instanceof Error) {
      return error.message;
    }

    return 'Error desconocido.';
  }

  private getStoredValue(storageKey: string): string | null {
    const value = sessionStorage.getItem(storageKey) ?? localStorage.getItem(storageKey);
    if (!value) {
      return null;
    }

    return value.trim().length > 0 ? value : null;
  }
}
